import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './outsourceFilterModel.css';

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

// ✅ FIX: stable default values.
// Before, `accountOptions = []` etc. created a NEW array on every render, so the
// useEffect below (which depended on those arrays) re-ran on every render,
// re-fetched the labs again and again, switched the list to "Loading labs..."
// and disabled it — that is why a lab could not be selected with the mouse.
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

function OutsourceFilterModel({
  show,
  handleClose,
  onFilterApply,
  accountOptions = EMPTY_ARRAY,        // array of labName strings (legacy)
  accountOptionsWithIds = EMPTY_ARRAY, // ✅ NEW: array of { labName, userLabId } objects (preferred)
  exportEntries = EMPTY_ARRAY,
  labInfo = EMPTY_OBJECT,
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState('All');
  const [accounts, setAccounts] = useState(['All']);
  const [format, setFormat] = useState('On Screen');

  const [fetchedLabs, setFetchedLabs] = useState([]);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [labsError, setLabsError] = useState('');

  const getUserLabId = () => {
    try {
      return localStorage.getItem('userLabId');
    } catch (e) {
      console.error('Error getting userLabId from localStorage:', e);
      return null;
    }
  };

  // ================================================================
  // ✅ HELPER: Normalize string for case/whitespace-insensitive matching
  // ================================================================
  const normalizeStr = (val) =>
    val === null || val === undefined ? '' : String(val).trim().toLowerCase();

  // ================================================================
  // ✅ BUILD LAB NAME → userLabId MAP
  //
  // Priority:
  //   1. Use `accountOptionsWithIds` if provided (preferred — carries IDs)
  //   2. Fall back to `fetchedLabs` (from source-labs API)
  //   3. Fall back to `accountOptions` (names only — no ID mapping possible)
  //
  // This map lets us translate a selected labName into its immutable
  // userLabId, so filtering works even when the lab's display name
  // changes over time.
  // ================================================================
  const labNameToIdMap = useMemo(() => {
    const map = {};

    // Preferred source: explicit { labName, userLabId } pairs
    (accountOptionsWithIds || []).forEach((item) => {
      if (!item) return;
      const name = item.labName || item.name || '';
      const id = item.userLabId || item.labId || '';
      if (name && id) {
        map[normalizeStr(name)] = String(id).trim();
      }
    });

    // Secondary: fetchedLabs from source-labs API
    (fetchedLabs || []).forEach((lab) => {
      const name = lab.labName || lab.name || '';
      const id = lab.userLabId || lab.labId || '';
      if (name && id) {
        const key = normalizeStr(name);
        if (!map[key]) map[key] = String(id).trim();
      }
    });

    return map;
  }, [accountOptionsWithIds, fetchedLabs]);

  // ================================================================
  // ✅ RESOLVE LAB NAMES → userLabIds
  // Returns an array of userLabIds corresponding to selected lab names.
  // ================================================================
  const resolveSelectedLabIds = (selectedLabNames) => {
    if (!selectedLabNames || selectedLabNames.length === 0) return [];
    return selectedLabNames
      .map((name) => labNameToIdMap[normalizeStr(name)])
      .filter(Boolean)
      .map((id) => String(id).trim());
  };

  // ================================================================
  // ✅ ENTRY MATCHER — userLabId-based with labName fallback
  //
  // Priority:
  //   1. If entry has userLabId AND we resolved IDs → match by userLabId
  //   2. Otherwise → fallback to normalized labName comparison
  // ================================================================
  const entryMatchesAccounts = (entry, resolvedIds, selectedNames) => {
    const entryUserLabId = entry.userLabId || entry.labId || '';
    const entryAccount = entry.account || entry.labName || '';

    // Primary: userLabId match (handles renamed labs correctly)
    if (entryUserLabId && resolvedIds.length > 0) {
      const normalizedEntryId = String(entryUserLabId).trim();
      if (resolvedIds.includes(normalizedEntryId)) {
        return true;
      }
    }

    // Fallback: labName match (backward compatibility for legacy data)
    const normalizedSelectedNames = selectedNames.map(normalizeStr);
    return normalizedSelectedNames.includes(normalizeStr(entryAccount));
  };

  // ✅ FIX: use primitive booleans as effect dependencies (not the arrays),
  // so the effect only runs when `show` or the availability of options changes —
  // never in a loop on every render.
  const hasLabsWithIds = !!(accountOptionsWithIds && accountOptionsWithIds.length > 0);
  const hasLabOptions = !!(accountOptions && accountOptions.length > 0);

  useEffect(() => {
    if (hasLabsWithIds || hasLabOptions) {
      // keep the same reference when already empty (avoids needless re-render)
      setFetchedLabs((prev) => (prev.length ? [] : prev));
      setLabsError('');
      setLoadingLabs(false);
      return;
    }

    if (!show) return;

    let isCancelled = false;

    const fetchSourceLabs = async () => {
      const userLabId = getUserLabId();
      if (!userLabId) {
        if (!isCancelled) {
          setLabsError('User identification not found. Please log in again.');
          setFetchedLabs([]);
        }
        return;
      }
      try {
        setLoadingLabs(true);
        setLabsError('');
        const response = await fetch(`${API_BASE_URL}/api/outsource-invoice/source-labs`, {
          headers: { userlabid: userLabId },
        });

        if (isCancelled) return;

        if (!response.ok) {
          if (response.status === 404) {
            setFetchedLabs([]);
          } else {
            throw new Error(`Failed to fetch labs: ${response.status}`);
          }
        } else {
          const result = await response.json();
          if (isCancelled) return;
          if (result.success && Array.isArray(result.data)) {
            setFetchedLabs(result.data);
          } else {
            setFetchedLabs([]);
          }
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching labs for filter:', err);
        setLabsError('Failed to load labs.');
        setFetchedLabs([]);
      } finally {
        if (!isCancelled) setLoadingLabs(false);
      }
    };

    fetchSourceLabs();

    return () => {
      isCancelled = true;
    };
  }, [show, hasLabsWithIds, hasLabOptions]);

  // ================================================================
  // ✅ BUILD FINAL LAB OPTIONS
  // Preference order: accountOptionsWithIds > accountOptions > fetchedLabs
  // ================================================================
  const finalLabOptions = useMemo(() => {
    if (accountOptionsWithIds && accountOptionsWithIds.length > 0) {
      return [
        'All',
        ...accountOptionsWithIds
          .map((item) => item?.labName || item?.name || '')
          .filter(Boolean),
      ];
    }

    if (accountOptions && accountOptions.length > 0) {
      return ['All', ...accountOptions.filter((a) => a !== 'All')];
    }

    return ['All', ...fetchedLabs.map((lab) => lab.labName)];
  }, [accountOptions, accountOptionsWithIds, fetchedLabs]);

  useEffect(() => {
    if (show) {
      setStartDate('');
      setEndDate('');
      setType('All');
      setAccounts(['All']);
      setFormat('On Screen');
    }
  }, [show]);

  // ✅ FIX: lab selection.
  // - "All" is replaced as soon as a specific lab is picked
  // - picking "All" again clears the specific labs
  // - Ctrl/Cmd + click still lets you select several labs
  const handleAccountChange = (e) => {
    const options = Array.from(e.target.selectedOptions, (option) => option.value);

    setAccounts((prev) => {
      if (options.length === 0) return ['All'];

      if (options.includes('All') && options.length > 1) {
        // "All" was already selected and a lab was added -> drop "All"
        // "All" was just added on top of labs -> keep only "All"
        return prev.includes('All')
          ? options.filter((o) => o !== 'All')
          : ['All'];
      }

      return options;
    });
  };

  // ================================================================
  // HELPERS (unchanged)
  // ================================================================
  const formatDateNumeric = (dateInput) => {
    if (!dateInput) return '';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  const formatNumberPlain = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '0';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(n)) return '0';
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatMonthYear = (dateInput) => {
    if (!dateInput) return 'All Time';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return 'All Time';
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatBillOfMonth = (start, end) => {
    if (!start && !end) return 'All Time';
    if (start && end && start.slice(0, 7) === end.slice(0, 7)) {
      return formatMonthYear(start);
    }
    if (start && end) {
      return `${formatDateNumeric(start)} → ${formatDateNumeric(end)}`;
    }
    return formatMonthYear(start || end);
  };

  const getBillToLabel = (entries) => {
    const uniqueAccounts = [
      ...new Set(
        entries
          .map((e) => (e.account || '').trim())
          .filter(Boolean)
      ),
    ];

    if (uniqueAccounts.length === 1) return uniqueAccounts[0];
    if (uniqueAccounts.length > 1) return `${uniqueAccounts.length} Labs`;

    const uniqueDestLabs = [
      ...new Set(
        entries
          .map(
            (e) =>
              e.destinationLabName ||
              e.destinationLab?.labName ||
              ''
          )
          .map((v) => v.trim())
          .filter(Boolean)
      ),
    ];

    if (uniqueDestLabs.length === 1) return uniqueDestLabs[0];
    if (uniqueDestLabs.length > 1) return `${uniqueDestLabs.length} Destination Labs`;

    return 'N/A';
  };

  const getInvoiceNumber = (entry) => {
    if (entry.invoiceNumber) return entry.invoiceNumber;

    const ref = entry.reference || '';
    const refs = ref.split(',').map((r) => r.trim()).filter(Boolean);
    const osRef = refs.find((r) => /^OS\d+/i.test(r));
    if (osRef) return osRef;

    if (
      entry.sourceType === 'payment' ||
      entry.sourceType === 'credit' ||
      entry.sourceType === 'debit' ||
      entry.sourceType === 'credit_payment'
    ) {
      return refs[0] || '';
    }

    return '';
  };

  const getTypeLabel = (entry) => {
    const t = (entry.type || '').trim();
    if (!t) return '';
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  };

  // ================================================================
  // BUILD LINE ITEMS (unchanged)
  // ================================================================
  const buildLineItems = (entries) => {
    const lines = [];

    entries.forEach((entry) => {
      const invoiceNumber = getInvoiceNumber(entry);
      const caseNumber = entry.caseNumber || '';
      const typeLabel = getTypeLabel(entry);

      const invoiceLines =
        entry.invoiceLines && Array.isArray(entry.invoiceLines) && entry.invoiceLines.length > 0
          ? entry.invoiceLines
          : null;

      if (invoiceLines) {
        invoiceLines.forEach((line) => {
          const qty = parseFloat(line.units) || 0;
          const amount = parseFloat(line.amount) || 0;
          const unitPrice = qty > 0 ? amount / qty : 0;

          lines.push({
            caseNumber,
            invoiceNumber,
            type: typeLabel,
            description:
              line.description ||
              entry.procedureName ||
              entry.procedure?.name ||
              entry.procedure?.procedureName ||
              '',
            unit: qty,
            unitPrice,
            total: amount,
          });
        });
      } else {
        const amount = parseFloat(entry.amount) || 0;
        const qty = parseFloat(entry.units) || 1;

        lines.push({
          caseNumber,
          invoiceNumber,
          type: typeLabel,
          description:
            entry.procedureName ||
            entry.procedure?.name ||
            entry.procedure?.procedureName ||
            entry.description ||
            typeLabel ||
            'Entry',
          unit: qty,
          unitPrice: qty > 0 ? amount / qty : amount,
          total: amount,
        });
      }
    });

    return lines;
  };

  // ================================================================
  // PDF EXPORT (unchanged)
  // ================================================================
  const exportToPDF = (entries, activeFilters) => {
    if (!entries || entries.length === 0) {
      alert('No entries to export.');
      return;
    }

    const lines = buildLineItems(entries);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;
    const headerTop = 40;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#000000');
    doc.text(labInfo.labName || 'Laboratory', marginX, headerTop);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let y = headerTop + 16;

    if (labInfo.address) {
      const addressLines = doc.splitTextToSize(labInfo.address, 240);
      doc.text(addressLines, marginX, y);
      y += addressLines.length * 12;
    }
    if (labInfo.phone) {
      doc.text(labInfo.phone, marginX, y);
      y += 12;
    }
    if (labInfo.email) {
      doc.setTextColor('#004aad');
      doc.text(labInfo.email, marginX, y);
      doc.setTextColor('#000000');
      y += 12;
    }

    const rightX = pageWidth - marginX;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DATE', rightX, headerTop, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    const todayForHeader = formatDateNumeric(new Date());
    doc.text(todayForHeader, rightX, headerTop + 14, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.text('Bill of Month', rightX, headerTop + 32, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    const billOfMonth = formatBillOfMonth(activeFilters?.startDate, activeFilters?.endDate);
    doc.text(billOfMonth, rightX, headerTop + 46, { align: 'right' });

    let billToY = headerTop + 70;
    doc.setFontSize(9);
    doc.setTextColor('#666666');
    doc.text('BILL TO', marginX, billToY);
    doc.setTextColor('#000000');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);

    const billToLabel = getBillToLabel(entries);
    doc.text(billToLabel, marginX, billToY + 16);

    const tableStartY = billToY + 40;

    const tableBody = lines.map((line, idx) => [
      idx + 1,
      line.caseNumber || '',
      line.invoiceNumber || '',
      line.type || '',
      line.description,
      line.unit,
      formatNumberPlain(line.unitPrice),
      formatNumberPlain(line.total),
    ]);

    const grandTotal = lines.reduce((sum, l) => sum + (parseFloat(l.total) || 0), 0);

    autoTable(doc, {
      startY: tableStartY,
      head: [[
        'Sr No',
        'Case No',
        'Invoice No',
        'Type',
        'Description',
        'Unit',
        'UNIT PRICE',
        'TOTAL',
      ]],
      body: tableBody,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 5,
        textColor: '#000000',
        lineColor: '#000000',
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [0, 74, 173],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 8,
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 32 },
        1: { halign: 'center', cellWidth: 48 },
        2: { halign: 'center', cellWidth: 55 },
        3: { halign: 'center', cellWidth: 45 },
        4: { halign: 'left',   cellWidth: 'auto' },
        5: { halign: 'center', cellWidth: 32 },
        6: { halign: 'right',  cellWidth: 55 },
        7: { halign: 'right',  cellWidth: 55 },
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      margin: { left: marginX, right: marginX },
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    const totalRowX = pageWidth - marginX;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('GRAND TOTAL', totalRowX - 90, finalY, { align: 'right' });
    doc.text(formatNumberPlain(grandTotal), totalRowX, finalY, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor('#888888');
    doc.text(
      `Generated on ${new Date().toLocaleString()}  |  Total entries: ${entries.length}  |  Total line items: ${lines.length}`,
      marginX,
      pageHeight - 20
    );

    const periodSlug =
      activeFilters?.startDate && activeFilters?.endDate
        ? `${activeFilters.startDate}_to_${activeFilters.endDate}`
        : 'All_Time';
    doc.save(`Outsource_Posting_${periodSlug}.pdf`);
  };

  // ================================================================
  // EXCEL EXPORT (unchanged)
  // ================================================================
  const exportToExcel = (entries, activeFilters) => {
    if (!entries || entries.length === 0) {
      alert('No entries to export.');
      return;
    }

    const lines = buildLineItems(entries);

    const todayForHeader = formatDateNumeric(new Date());
    const billOfMonth = formatBillOfMonth(activeFilters?.startDate, activeFilters?.endDate);

    const billToLabel = getBillToLabel(entries);

    const headerRows = [
      [labInfo.labName || 'Laboratory'],
      [labInfo.address || ''],
      [labInfo.phone || ''],
      [labInfo.email || ''],
      [],
      ['DATE', todayForHeader, '', 'Bill of Month', billOfMonth],
      ['BILL TO', billToLabel],
      [],
      ['Sr No', 'Case No', 'Invoice No', 'Type', 'Description', 'Unit', 'UNIT PRICE', 'TOTAL'],
    ];

    const bodyRows = lines.map((line, idx) => [
      idx + 1,
      line.caseNumber || '',
      line.invoiceNumber || '',
      line.type || '',
      line.description,
      line.unit,
      line.unitPrice,
      line.total,
    ]);

    const grandTotal = lines.reduce((sum, l) => sum + (parseFloat(l.total) || 0), 0);
    const footerRows = [[], ['', '', '', '', '', '', 'GRAND TOTAL', grandTotal]];

    const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...bodyRows, ...footerRows]);

    worksheet['!cols'] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 16 },
      { wch: 12 },
      { wch: 40 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Outsource Posting');

    const periodSlug =
      activeFilters?.startDate && activeFilters?.endDate
        ? `${activeFilters.startDate}_to_${activeFilters.endDate}`
        : 'All_Time';
    XLSX.writeFile(workbook, `Outsource_Posting_${periodSlug}.xlsx`);
  };

  // ================================================================
  // ✅ Compute filtered entries — userLabId-based with labName fallback
  // ================================================================
  const computeFilteredEntries = (data, appliedFilters) => {
    let result = [...data];

    if (appliedFilters.startDate) {
      const start = new Date(appliedFilters.startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter((e) => e.postDate && new Date(e.postDate) >= start);
    }
    if (appliedFilters.endDate) {
      const end = new Date(appliedFilters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((e) => e.postDate && new Date(e.postDate) <= end);
    }
    if (appliedFilters.type && appliedFilters.type !== 'All') {
      result = result.filter(
        (e) => e.type && normalizeStr(e.type) === normalizeStr(appliedFilters.type)
      );
    }

    // ============================================================
    // ✅ FIX: userLabId-based account matching
    //
    // Priority:
    //   1. Resolve selected lab names → userLabIds via labNameToIdMap
    //   2. Match entries by `entry.userLabId` (immutable — immune to renames)
    //   3. Fallback to labName comparison for legacy entries without userLabId
    // ============================================================
    const selectedAccounts = appliedFilters.accounts || [];
    const isAllSelected =
      selectedAccounts.length === 0 ||
      selectedAccounts.includes('All');

    if (!isAllSelected) {
      const selectedLabNames = selectedAccounts.filter((a) => a && a !== 'All');
      const resolvedIds = resolveSelectedLabIds(selectedLabNames);

      result = result.filter((entry) =>
        entryMatchesAccounts(entry, resolvedIds, selectedLabNames)
      );
    } else if (
      appliedFilters.account &&
      appliedFilters.account !== 'All' &&
      (!appliedFilters.accounts || appliedFilters.accounts.length === 0)
    ) {
      const selectedLabNames = appliedFilters.account
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean);
      const resolvedIds = resolveSelectedLabIds(selectedLabNames);

      result = result.filter((entry) =>
        entryMatchesAccounts(entry, resolvedIds, selectedLabNames)
      );
    }

    return result;
  };

  // ================================================================
  // APPLY FILTER
  // ================================================================
  const applyFilter = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert('Start Date cannot be after End Date');
      return;
    }

    const includesAll = accounts.includes('All') || accounts.length === 0;

    // ✅ Resolve selected lab names → userLabIds for the filter payload
    const selectedLabNames = includesAll
      ? []
      : accounts.filter((a) => a && a !== 'All');
    const selectedUserLabIds = resolveSelectedLabIds(selectedLabNames);

    const filterData = {
      startDate: startDate || '',
      endDate: endDate || '',
      type: type || 'All',
      account: includesAll ? 'All' : accounts.join(','),
      accounts: includesAll ? ['All'] : accounts,
      // ✅ NEW: pre-resolved userLabIds for downstream consumers
      selectedUserLabIds,
      selectedLabNames,
      format: format || 'On Screen',
    };

    console.log('Applying filter (userLabId-aware):', filterData);

    if (onFilterApply) {
      onFilterApply(filterData);
    }

    if (format === 'PDF') {
      const filtered = computeFilteredEntries(exportEntries, filterData);
      exportToPDF(filtered, filterData);
    } else if (format === 'Excel') {
      const filtered = computeFilteredEntries(exportEntries, filterData);
      exportToExcel(filtered, filterData);
    }

    handleClose();
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setType('All');
    setAccounts(['All']);
    setFormat('On Screen');
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Header className="filter-modal-header" closeButton closeVariant="white">
        <Modal.Title className="filter-header-title">Filter</Modal.Title>
      </Modal.Header>

      <Modal.Body className="filter-modal-body">
        <h4 className="filter-modal-title">Parameters</h4>

        <div className="filter-title-border"></div>

        <div className="filter-grey-box">
          <Form>
            {/* Start Date */}
            <div className="filter-form-group">
              <Form.Label className="filter-form-label">
                Start Date <span className="required-star">*</span>
              </Form.Label>
              <Form.Control
                className="filter-form-control"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
              />
            </div>

            {/* End Date */}
            <div className="filter-form-group">
              <Form.Label className="filter-form-label">
                End Date <span className="required-star">*</span>
              </Form.Label>
              <Form.Control
                className="filter-form-control"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
              />
            </div>

            {/* Type Dropdown */}
            <div className="filter-form-group">
              <Form.Label className="filter-form-label">
                Type <span className="required-star">*</span>
              </Form.Label>
              <Form.Select
                className="filter-form-control filter-select-control"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Invoice">Invoice</option>
                <option value="Payment">Payment</option>
                <option value="Credit">Credit</option>
                <option value="Debit">Debit</option>
              </Form.Select>
            </div>

            {/* Labs List Box */}
            <div className="filter-form-group filter-account-group">
              <Form.Label className="filter-form-label">Lab</Form.Label>

              <Form.Control
                className="filter-account-control"
                as="select"
                multiple
                value={accounts}
                onChange={handleAccountChange}
                disabled={loadingLabs}
              >
                {loadingLabs ? (
                  <option value="All">Loading labs...</option>
                ) : (
                  finalLabOptions.map((lab, index) => (
                    <option key={index} value={lab}>
                      {lab}
                    </option>
                  ))
                )}
              </Form.Control>
              {labsError && (
                <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
                  {labsError}
                </small>
              )}
            </div>

            {/* Format Dropdown */}
            <div className="filter-form-group filter-last-group">
              <Form.Label className="filter-form-label">
                Format <span className="required-star">*</span>
              </Form.Label>
              <Form.Select
                className="filter-form-control filter-select-control"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
              >
                <option value="On Screen">On Screen</option>
                <option value="PDF">PDF</option>
                <option value="Excel">Excel</option>
              </Form.Select>
            </div>
          </Form>
        </div>

        <div
          className="filter-button-container"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Button className="filter-btn" onClick={handleReset}>
            Reset
          </Button>
          <Button className="filter-btn" onClick={applyFilter}>
            Filter
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default OutsourceFilterModel;